import type { Feature, FeatureCollection, LineString, MultiPolygon, Point, Polygon } from "geojson";
import {
  AttributionControl,
  GeoJSONSource,
  LngLatBounds,
  Map as MapLibreMap,
  type MapLayerMouseEvent,
  NavigationControl,
} from "maplibre-gl";
import { Focus } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { LIGHT_MAP_STYLE, PLACE_LABEL_LAYER_ID } from "../mapStyle";
import type { Flow, OdData } from "../types";
import { flowCurve, flowId, formatTrips, regionByName, stableOffset } from "../utils";

interface FlowMapProps {
  data: OdData;
  flows: Flow[];
  flowLimit: number;
  origins: string[];
  destinations: string[];
  hoveredRegion: string | null;
  hoveredFlowId: string | null;
  onHoverRegion: (name: string | null) => void;
  onHoverFlow: (id: string | null) => void;
  onSelectRegion: (name: string, role: "origin" | "destination") => void;
}

interface FlowFeatureProperties {
  id: string;
  origin: string;
  destination: string;
  trips: number;
  normalized: number;
}

interface OverlayRegion {
  name: string;
  path: string;
  role: string;
  selected: boolean;
  hovered: boolean;
}

interface OverlayFlow extends FlowFeatureProperties {
  path: string;
  width: number;
}

interface ProjectedOverlay {
  width: number;
  height: number;
  regions: OverlayRegion[];
  flows: OverlayFlow[];
}

function linePath(map: MapLibreMap, coordinates: Array<[number, number]>): string {
  return coordinates.map((coordinate, index) => {
    const point = map.project(coordinate);
    return `${index === 0 ? "M" : "L"}${point.x.toFixed(1)},${point.y.toFixed(1)}`;
  }).join(" ");
}

function polygonPath(map: MapLibreMap, geometry: Polygon | MultiPolygon): string {
  const polygons = geometry.type === "Polygon" ? [geometry.coordinates] : geometry.coordinates;
  return polygons.map((polygon) => polygon.map((ring) => `${linePath(map, ring as Array<[number, number]>)} Z`).join(" ")).join(" ");
}

function geojsonBounds(data: OdData): LngLatBounds {
  const bounds = new LngLatBounds();
  const visit = (value: unknown) => {
    if (!Array.isArray(value)) return;
    if (value.length >= 2 && typeof value[0] === "number" && typeof value[1] === "number") {
      bounds.extend([value[0], value[1]]);
      return;
    }
    value.forEach(visit);
  };
  data.geojson.features.forEach((feature) => visit(feature.geometry.coordinates));
  return bounds;
}

function arrowImage(): ImageData {
  const canvas = document.createElement("canvas");
  canvas.width = 36;
  canvas.height = 36;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Canvas is unavailable");
  context.fillStyle = "#c92843";
  context.strokeStyle = "rgba(255,255,255,.96)";
  context.lineWidth = 2.4;
  context.beginPath();
  context.moveTo(18, 3);
  context.lineTo(31, 27);
  context.lineTo(18, 22);
  context.lineTo(5, 27);
  context.closePath();
  context.fill();
  context.stroke();
  return context.getImageData(0, 0, 36, 36);
}

function bearingAt(points: Array<[number, number]>, index: number): number {
  const start = points[Math.max(0, index - 1)];
  const end = points[Math.min(points.length - 1, index + 1)];
  const latitudeScale = Math.cos(((start[1] + end[1]) / 2) * Math.PI / 180);
  return Math.atan2((end[0] - start[0]) * latitudeScale, end[1] - start[1]) * 180 / Math.PI;
}

function propertiesFromEvent(event: MapLayerMouseEvent): FlowFeatureProperties | null {
  const properties = event.features?.[0]?.properties;
  if (!properties) return null;
  return {
    id: String(properties.id),
    origin: String(properties.origin),
    destination: String(properties.destination),
    trips: Number(properties.trips),
    normalized: Number(properties.normalized),
  };
}

export function FlowMap({
  data,
  flows,
  flowLimit,
  origins,
  destinations,
  hoveredRegion,
  hoveredFlowId,
  onHoverRegion,
  onHoverFlow,
  onSelectRegion,
}: FlowMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const animationRef = useRef<number | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [pointerFlow, setPointerFlow] = useState<FlowFeatureProperties | null>(null);
  const [overlay, setOverlay] = useState<ProjectedOverlay>({ width: 1, height: 1, regions: [], flows: [] });
  const lookup = useMemo(() => regionByName(data.regions), [data.regions]);
  const visibleFlows = useMemo(() => flows.slice(0, flowLimit), [flowLimit, flows]);
  const maximum = Math.max(1, ...visibleFlows.map((flow) => flow.t));
  const explicitOrigins = useMemo(() => new Set(origins), [origins]);
  const explicitDestinations = useMemo(() => new Set(destinations), [destinations]);

  const flowFeatures = useMemo(() => visibleFlows.flatMap((flow): Array<Feature<LineString, FlowFeatureProperties>> => {
    const origin = lookup.get(flow.o);
    const destination = lookup.get(flow.d);
    if (!origin || !destination) return [];
    return [{
      type: "Feature",
      properties: {
        id: flowId(flow),
        origin: flow.o,
        destination: flow.d,
        trips: flow.t,
        normalized: Math.sqrt(flow.t / maximum),
      },
      geometry: { type: "LineString", coordinates: flowCurve(origin.center, destination.center) },
    }];
  }), [lookup, maximum, visibleFlows]);

  const flowGeojson = useMemo<FeatureCollection<LineString, FlowFeatureProperties>>(
    () => ({ type: "FeatureCollection", features: flowFeatures }),
    [flowFeatures],
  );
  const selectedHoverFeature = useMemo(
    () => flowFeatures.find((feature) => feature.properties.id === hoveredFlowId) ?? null,
    [flowFeatures, hoveredFlowId],
  );
  const activeHover = pointerFlow ?? selectedHoverFeature?.properties ?? null;
  const activeHoverId = pointerFlow?.id ?? hoveredFlowId;
  const hoverGeojson = useMemo<FeatureCollection<LineString, FlowFeatureProperties>>(
    () => ({ type: "FeatureCollection", features: selectedHoverFeature ? [selectedHoverFeature] : pointerFlow ? flowFeatures.filter((feature) => feature.properties.id === pointerFlow.id) : [] }),
    [flowFeatures, pointerFlow, selectedHoverFeature],
  );

  const decoratedRegions = useMemo(() => {
    const originTotals = new Map<string, number>();
    const destinationTotals = new Map<string, number>();
    visibleFlows.forEach((flow) => {
      originTotals.set(flow.o, (originTotals.get(flow.o) ?? 0) + flow.t);
      destinationTotals.set(flow.d, (destinationTotals.get(flow.d) ?? 0) + flow.t);
    });
    const regionMaximum = Math.max(1, ...originTotals.values(), ...destinationTotals.values());
    return {
      type: "FeatureCollection" as const,
      features: data.geojson.features.map((feature) => {
        const name = String(feature.properties?.name ?? "");
        const originVolume = originTotals.get(name) ?? 0;
        const destinationVolume = destinationTotals.get(name) ?? 0;
        const activeOrigin = originVolume > 0;
        const activeDestination = destinationVolume > 0;
        const role = activeOrigin && activeDestination ? "combined" : activeOrigin ? "origin" : activeDestination ? "destination" : "none";
        return {
          ...feature,
          properties: {
            ...feature.properties,
            role,
            selected: explicitOrigins.has(name) || explicitDestinations.has(name),
            hovered: hoveredRegion === name,
            isAirport: name === data.meta.airportRegion,
            normalized: Math.sqrt(Math.max(originVolume, destinationVolume) / regionMaximum),
          },
        };
      }),
    };
  }, [data.geojson.features, data.meta.airportRegion, explicitDestinations, explicitOrigins, hoveredRegion, visibleFlows]);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new MapLibreMap({
      container: containerRef.current,
      style: LIGHT_MAP_STYLE,
      bounds: geojsonBounds(data),
      fitBoundsOptions: { padding: 44 },
      minZoom: 8,
      maxZoom: 16,
      attributionControl: false,
    });
    mapRef.current = map;
    map.addControl(new AttributionControl({ compact: true }), "bottom-left");
    map.addControl(new NavigationControl({ showCompass: false }), "bottom-right");

    const regionName = (event: MapLayerMouseEvent) => String(event.features?.[0]?.properties?.name ?? "");
    map.on("load", () => {
      map.addImage("flow-arrow", arrowImage(), { pixelRatio: 2 });
      map.addSource("regions", { type: "geojson", data: decoratedRegions, promoteId: "name" });
      map.addLayer({
        id: "region-fill",
        type: "fill",
        source: "regions",
        paint: {
          "fill-color": [
            "case",
            ["all", ["boolean", ["get", "selected"], false], ["==", ["get", "role"], "origin"]], "#2b8167",
            ["all", ["boolean", ["get", "selected"], false], ["==", ["get", "role"], "destination"]], "#c92843",
            ["all", ["boolean", ["get", "selected"], false], ["==", ["get", "role"], "combined"]], "#75568f",
            ["==", ["get", "role"], "origin"], "#b7d9cc",
            ["==", ["get", "role"], "destination"], "#efbac3",
            ["==", ["get", "role"], "combined"], "#d7c5e2",
            "#e4e7e5",
          ],
          "fill-opacity": 0,
        },
      }, PLACE_LABEL_LAYER_ID);
      map.addLayer({
        id: "region-outline",
        type: "line",
        source: "regions",
        paint: {
          "line-color": ["case", ["boolean", ["get", "hovered"], false], "#17232a", ["boolean", ["get", "selected"], false], "#5b2634", ["boolean", ["get", "isAirport"], false], "#8f2337", "#6b767c"],
          "line-width": ["case", ["boolean", ["get", "hovered"], false], 2.5, ["boolean", ["get", "selected"], false], 1.8, ["boolean", ["get", "isAirport"], false], 1.5, 0.7],
          "line-opacity": 0,
        },
      }, PLACE_LABEL_LAYER_ID);
      map.addSource("flows", { type: "geojson", data: flowGeojson, lineMetrics: true });
      map.addLayer({
        id: "flow-casing",
        type: "line",
        source: "flows",
        paint: { "line-color": "rgba(255,255,255,.82)", "line-opacity": 0, "line-width": ["interpolate", ["linear"], ["get", "normalized"], 0, 2.4, 1, 9.4] },
        layout: { "line-cap": "round", "line-join": "round" },
      }, PLACE_LABEL_LAYER_ID);
      map.addLayer({
        id: "flow-lines",
        type: "line",
        source: "flows",
        paint: {
          "line-gradient": ["interpolate", ["linear"], ["line-progress"], 0, "#257e64", 0.55, "#547087", 1, "#c92843"],
          "line-opacity": 0,
          "line-width": ["interpolate", ["linear"], ["get", "normalized"], 0, 1.1, 1, 7.4],
        },
        layout: { "line-cap": "round", "line-join": "round" },
      }, PLACE_LABEL_LAYER_ID);
      map.addLayer({ id: "flow-hit", type: "line", source: "flows", paint: { "line-color": "rgba(0,0,0,0)", "line-width": 16 } }, PLACE_LABEL_LAYER_ID);
      map.addSource("hover-flow", { type: "geojson", data: hoverGeojson, lineMetrics: true });
      map.addLayer({ id: "hover-flow-halo", type: "line", source: "hover-flow", paint: { "line-color": "rgba(255,255,255,.97)", "line-opacity": 0, "line-width": ["interpolate", ["linear"], ["get", "normalized"], 0, 6, 1, 13] }, layout: { "line-cap": "round", "line-join": "round" } }, PLACE_LABEL_LAYER_ID);
      map.addLayer({ id: "hover-flow-line", type: "line", source: "hover-flow", paint: { "line-gradient": ["interpolate", ["linear"], ["line-progress"], 0, "#176b52", 0.55, "#465f77", 1, "#a91935"], "line-opacity": 0, "line-width": ["interpolate", ["linear"], ["get", "normalized"], 0, 2.8, 1, 9.2] }, layout: { "line-cap": "round", "line-join": "round" } }, PLACE_LABEL_LAYER_ID);
      map.addSource("flow-particles", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
      map.addLayer({
        id: "flow-particles",
        type: "symbol",
        source: "flow-particles",
        layout: { "icon-image": "flow-arrow", "icon-size": ["interpolate", ["linear"], ["get", "normalized"], 0, 0.32, 1, 0.52], "icon-rotate": ["get", "bearing"], "icon-rotation-alignment": "map", "icon-allow-overlap": true },
        paint: { "icon-opacity": 0 },
      }, PLACE_LABEL_LAYER_ID);

      map.on("mouseenter", "region-fill", (event) => { map.getCanvas().style.cursor = "pointer"; const name = regionName(event); if (name) onHoverRegion(name); });
      map.on("mouseleave", "region-fill", () => { map.getCanvas().style.cursor = ""; onHoverRegion(null); });
      map.on("click", "region-fill", (event) => { const name = regionName(event); if (name) onSelectRegion(name, event.originalEvent.shiftKey ? "destination" : "origin"); });
      map.on("mouseenter", "flow-hit", (event) => { map.getCanvas().style.cursor = "crosshair"; const properties = propertiesFromEvent(event); setPointerFlow(properties); onHoverFlow(properties?.id ?? null); });
      map.on("mousemove", "flow-hit", (event) => { const properties = propertiesFromEvent(event); setPointerFlow(properties); onHoverFlow(properties?.id ?? null); });
      map.on("mouseleave", "flow-hit", () => { map.getCanvas().style.cursor = ""; setPointerFlow(null); onHoverFlow(null); });
      setMapLoaded(true);
    });

    const observer = new ResizeObserver(() => map.resize());
    observer.observe(containerRef.current);
    return () => {
      observer.disconnect();
      if (animationRef.current !== null) cancelAnimationFrame(animationRef.current);
      map.remove();
      mapRef.current = null;
    };
    // The Chennai zone geometry is immutable; changing controls updates GeoJSON sources.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapLoaded || !map) return;
    (map.getSource("regions") as GeoJSONSource | undefined)?.setData(decoratedRegions);
    (map.getSource("flows") as GeoJSONSource | undefined)?.setData(flowGeojson);
    (map.getSource("hover-flow") as GeoJSONSource | undefined)?.setData(hoverGeojson);
  }, [decoratedRegions, flowGeojson, hoverGeojson, mapLoaded]);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapLoaded || !map) return;
    let frame: number | null = null;
    const projectOverlay = () => {
      if (frame !== null) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        const container = map.getContainer();
        setOverlay({
          width: Math.max(1, container.clientWidth),
          height: Math.max(1, container.clientHeight),
          regions: decoratedRegions.features.map((feature) => ({
            name: String((feature.properties as Record<string, unknown> | null)?.name ?? ""),
            path: polygonPath(map, feature.geometry),
            role: String(feature.properties?.role ?? "none"),
            selected: Boolean(feature.properties?.selected),
            hovered: Boolean(feature.properties?.hovered),
          })),
          flows: flowFeatures.map((feature) => ({
            ...feature.properties,
            path: linePath(map, feature.geometry.coordinates as Array<[number, number]>),
            width: 1.3 + feature.properties.normalized * 6.3,
          })),
        });
      });
    };
    projectOverlay();
    map.on("move", projectOverlay);
    map.on("resize", projectOverlay);
    return () => {
      map.off("move", projectOverlay);
      map.off("resize", projectOverlay);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [decoratedRegions, flowFeatures, mapLoaded]);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapLoaded || !map) return;
    const source = map.getSource("flow-particles") as GeoJSONSource | undefined;
    if (!source) return;
    if (animationRef.current !== null) cancelAnimationFrame(animationRef.current);
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const updateParticles = (now: number) => {
      const features: Array<Feature<Point, { bearing: number; normalized: number }>> = [];
      flowFeatures.slice(0, 32).forEach((feature) => {
        const coordinates = feature.geometry.coordinates as Array<[number, number]>;
        const particleCount = feature.properties.normalized > 0.72 ? 3 : feature.properties.normalized > 0.4 ? 2 : 1;
        for (let particle = 0; particle < particleCount; particle += 1) {
          const seed = stableOffset(`${feature.properties.id}-${particle}`);
          const progress = reducedMotion ? 0.66 : (now / 5600 + seed) % 1;
          const index = Math.min(coordinates.length - 2, Math.max(1, Math.floor(progress * (coordinates.length - 1))));
          features.push({ type: "Feature", properties: { bearing: bearingAt(coordinates, index), normalized: feature.properties.normalized }, geometry: { type: "Point", coordinates: coordinates[index] } });
        }
      });
      source.setData({ type: "FeatureCollection", features });
      if (!reducedMotion) animationRef.current = requestAnimationFrame(updateParticles);
    };
    updateParticles(performance.now());
    return () => {
      if (animationRef.current !== null) cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    };
  }, [flowFeatures, mapLoaded]);

  return (
    <div className="map-wrap">
      <div ref={containerRef} className="map-canvas" aria-label="Interactive Chennai origin-destination map" />
      <svg className="flow-svg-overlay" viewBox={`0 0 ${overlay.width} ${overlay.height}`} aria-hidden="true">
        <defs>
          <linearGradient id="flow-direction-gradient" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#257e64" />
            <stop offset="55%" stopColor="#547087" />
            <stop offset="100%" stopColor="#c92843" />
          </linearGradient>
          <marker id="flow-arrowhead" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto" markerUnits="userSpaceOnUse">
            <path d="M0,0 L8,4 L0,8 Z" fill="#c92843" stroke="white" strokeWidth="0.8" />
          </marker>
        </defs>
        <g className="svg-regions">
          {overlay.regions.map((region) => <path key={region.name} d={region.path} className={`svg-region role-${region.role}${region.selected ? " is-selected" : ""}${region.hovered ? " is-hovered" : ""}`} fillRule="evenodd" />)}
        </g>
        <g className="svg-flows">
          {overlay.flows.map((flow, index) => {
            const active = activeHoverId === flow.id;
            return (
              <g key={flow.id} className={active ? "is-active" : ""}>
                <path d={flow.path} className="svg-flow-casing" style={{ strokeWidth: flow.width + (active ? 5 : 2.2) }} />
                <path d={flow.path} className="svg-flow-line" markerEnd="url(#flow-arrowhead)" style={{ strokeWidth: flow.width + (active ? 2 : 0) }} />
                <path
                  d={flow.path}
                  className="svg-flow-hit"
                  style={{ strokeWidth: Math.max(16, flow.width + 10) }}
                  onMouseEnter={() => { setPointerFlow(flow); onHoverFlow(flow.id); }}
                  onMouseLeave={() => { setPointerFlow(null); onHoverFlow(null); }}
                />
                {index < 24 && !window.matchMedia("(prefers-reduced-motion: reduce)").matches && (
                  <circle className="svg-flow-particle" r={2.2 + flow.normalized * 1.8}>
                    <animateMotion dur={`${6.2 - flow.normalized * 1.8}s`} begin={`${-(stableOffset(flow.id) * 4).toFixed(2)}s`} repeatCount="indefinite" path={flow.path} />
                  </circle>
                )}
              </g>
            );
          })}
        </g>
      </svg>
      <div className="map-tools" aria-label="Map tools"><button className="icon-button map-tool" type="button" aria-label="Fit Chennai study area" onClick={() => mapRef.current?.fitBounds(geojsonBounds(data), { padding: 44, duration: 240 })}><Focus aria-hidden="true" /></button></div>
      <div className="map-instruction"><span className="live-dot" />Click for origin · Shift-click for destination · Hover a flow for trips</div>
      {hoveredRegion && <div className="hover-label">{lookup.get(hoveredRegion)?.shortName ?? hoveredRegion}</div>}
      {activeHover && (
        <div className="flow-tooltip" role="tooltip">
          <span><small>Origin</small>{lookup.get(activeHover.origin)?.shortName ?? activeHover.origin}</span>
          <b aria-hidden="true">→</b>
          <span><small>Destination</small>{lookup.get(activeHover.destination)?.shortName ?? activeHover.destination}</span>
          <strong>{formatTrips(activeHover.trips)} <small>factored trips / average weekday</small></strong>
        </div>
      )}
      <div className="flow-legend" aria-label="Flow encoding"><span><i className="legend-line thin" />Lower volume</span><span><i className="legend-line thick" />Higher volume</span></div>
    </div>
  );
}
